import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { motion, AnimatePresence } from "framer-motion";
import { useLanguage } from "@/hooks/useLanguage";

const CONSENT_KEY = "geposit-cookie-consent";

type ConsentValue = "accepted" | "rejected";

export function CookieBanner() {
  const { t } = useTranslation();
  const { getLocalizedPath } = useLanguage();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem(CONSENT_KEY);
    if (!stored) {
      // Small delay so the banner doesn't flash immediately on load
      const timer = setTimeout(() => setVisible(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleConsent = (value: ConsentValue) => {
    localStorage.setItem(CONSENT_KEY, value);
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          className="fixed bottom-0 inset-x-0 z-50 p-4 sm:p-6"
        >
          <div className="container mx-auto max-w-3xl">
            <div className="bg-card border border-border rounded-xl shadow-lg p-5 sm:p-6">
              <div className="flex items-start gap-4">
                <div className="hidden sm:flex shrink-0 h-10 w-10 rounded-full bg-primary/10 items-center justify-center">
                  <Cookie className="h-5 w-5 text-primary" />
                </div>

                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-foreground text-sm mb-1">
                    {t("cookieBanner.title")}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                    {t("cookieBanner.description")}{" "}
                    <Link
                      to={getLocalizedPath("/integritetspolicy")}
                      className="text-primary hover:text-primary/80 underline underline-offset-2 transition-colors"
                    >
                      {t("cookieBanner.readMore")}
                    </Link>
                  </p>

                  <div className="flex flex-wrap gap-3">
                    <Button
                      onClick={() => handleConsent("accepted")}
                      size="sm"
                      className="bg-coral-interactive hover:bg-coral-interactive/90 text-coral-foreground"
                    >
                      {t("cookieBanner.accept")}
                    </Button>
                    <Button
                      onClick={() => handleConsent("rejected")}
                      variant="outline"
                      size="sm"
                    >
                      {t("cookieBanner.reject")}
                    </Button>
                  </div>
                </div>

                <button
                  onClick={() => handleConsent("rejected")}
                  className="shrink-0 text-muted-foreground hover:text-foreground transition-colors p-2 -m-1 min-w-[44px] min-h-[44px] flex items-center justify-center"
                  aria-label={t("cookieBanner.reject")}
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
